import { FieldModel, Integer, ResourceDetail, ResourceDetailInputBase } from '@cibo/core'
import { DataGridPro, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Box, MenuItem, Select, styled, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid-pro'
import { assocPath, path } from 'ramda'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldColumnAttributes, FieldNameCell } from '../../../components/FieldColumns'
import { useBatchedFields, useUpdateMultipleFields } from '../../../queries'
import { useRemoveDetails, useSaveBulkDetails } from '../../DetailEditing'
import { BULK_ANSWERS, BulkAnswer } from './BooleanRollupRowInput'

const StyledDataGridPro = styled(DataGridPro)(() => ({
  '.MuiDataGrid-cell': {
    padding: 0,
    position: 'relative',
  },
  '.MuiSelect': { boxShadow: 'none' },
  '.MuiDataGrid-pinnedRows': {
    backgroundColor: 'unset',
    boxShadow: 'unset',
  },
})) as unknown as typeof DataGridPro

type BooleanAnswerRow = {
  id: string
  field: FieldModel
  answer?: BulkAnswer
  immutable: boolean
}

export type BooleanRollupRowTableProps<T extends ResourceDetail = ResourceDetail> = {
  detailKey?: keyof T['input'] | keyof T['result']
  ns: string
  answerValues?: Partial<Record<BulkAnswer, { value: any; label?: ReactNode }>>
} & ResourceDetailFeatureTaskEditorProps<T>

export const BooleanRollupRowTable = <T extends ResourceDetail = ResourceDetail>({
  answerValues = { yes: { value: 'yes' }, no: { value: 'no' }, unknown: { value: 'unknown' } },
  detailRequirements: [{ traitId, year, resultsOnly }],
  detailKey,
  onError,
  onSuccess,
  onUpdating,
  resourceIds,
}: BooleanRollupRowTableProps<T>) => {
  const { t } = useTranslation('@cibo/landmanager/BooleanRollup')
  const gridApi = useGridApiRef()

  const fieldModels = useBatchedFields(resourceIds)
  const [bulkAnswer, setBulkAnswer] = useState<BulkAnswer | undefined>()
  const updateFields = useUpdateMultipleFields()

  const saveBulkDetails = useSaveBulkDetails()
  const removeDetails = useRemoveDetails()

  const valuePath = useMemo(() => {
    const valuePath: Array<string> = [resultsOnly ? 'result' : 'input']
    if (detailKey) valuePath.push(detailKey as string)
    return valuePath
  }, [detailKey, resultsOnly])

  const answerForDetail = useCallback(
    (detail: ResourceDetail) => {
      if (!answerValues) return
      const value = path(valuePath, detail)
      if (typeof value === 'undefined') return

      for (const answer of BULK_ANSWERS) {
        if (value === answerValues[answer]?.value) return answer
      }
    },
    [answerValues, valuePath]
  )

  const detailForAnswer = useCallback(
    (answer: BulkAnswer) => {
      const detail = {
        traitId,
        year,
      }

      return assocPath(valuePath, answerValues[answer]?.value)(detail)
    },
    [traitId, year]
  )

  const changeFieldAnswer = useCallback(
    (params: GridRenderCellParams, answer: BulkAnswer | 'remove') => {
      gridApi.current?.updateRows([{ id: params.id, answer: answer === 'remove' ? '' : answer }])

      const answers = new Set<BulkAnswer>()
      const rowData = gridApi.current?.getSortedRows()
      rowData?.forEach(row => answers.add(row.answer))
      if (answers.size === 1) {
        setBulkAnswer(Array.from(answers)[0])
      } else {
        setBulkAnswer(undefined)
      }

      const template = { traitId, year }

      const newDetail =
        answer === 'remove'
          ? template
          : assocPath(valuePath, answerValues?.[answer]?.value, template)

      const updates = [{ resourceId: `${params.id}`, details: [newDetail] }]

      onUpdating?.()
      updateFields
        .mutateAsync(updates)
        .then(() => onSuccess?.())
        .catch(onError)
    },
    [gridApi.current, updateFields, setBulkAnswer]
  )

  const changeBulkAnswer = useCallback(
    (answer: BulkAnswer | 'remove') => {
      const rowData = gridApi.current?.getSortedRows()

      if (answer === 'remove') {
        setBulkAnswer(undefined)
        const fieldsWithDetail: string[] = []

        rowData?.forEach(row => {
          if (row.answer !== '') {
            fieldsWithDetail.push(row.id)
            gridApi.current?.updateRows([{ id: row.id, answer: '' }])
          }
        })

        onUpdating?.()
        removeDetails
          .mutateAsync({ resourceIds: fieldsWithDetail, traitId, year: year as Integer })
          .catch(onError)
          .then(onSuccess)
      } else {
        setBulkAnswer(answer)
        const fieldsToUpdate: string[] = []

        rowData?.forEach(row => {
          if (row.answer !== answer) {
            gridApi.current?.updateRows([{ id: row.id, answer }])
            fieldsToUpdate.push(row.id)
          }
        })

        onUpdating?.()
        saveBulkDetails
          .mutateAsync({
            resourceIds: fieldsToUpdate,
            details: [detailForAnswer(answer) as ResourceDetail<ResourceDetailInputBase, any>],
          })
          .catch(onError)
          .then(onSuccess)
      }
    },
    [gridApi.current, setBulkAnswer]
  )

  const handleChangeAnswer = useCallback(
    (params: GridRenderCellParams, answer: BulkAnswer | 'remove') => {
      if (params.id === 'bulkAnswerRow') {
        changeBulkAnswer(answer)
      } else {
        changeFieldAnswer(params, answer)
      }
    },
    [changeBulkAnswer, changeFieldAnswer]
  )

  const yesLabel = useMemo(() => answerValues['yes']?.label || t('yes'), [answerValues, t])
  const noLabel = useMemo(() => answerValues['no']?.label || t('no'), [answerValues, t])

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t('field'),
        hideable: false,
        flex: 3,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<FieldModel>) => (
          <Box sx={{ marginLeft: 2 }}>
            {params.row.id === 'bulkAnswerRow' ? (
              <Typography sx={{ fontWeight: 'bold', my: 2 }}>{t('answerForAllFields')}</Typography>
            ) : (
              <FieldNameCell {...params} />
            )}
          </Box>
        ),
      },
      {
        field: 'answer',
        headerName: t('answer'),
        minWidth: 170,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Select
              fullWidth
              onChange={event => handleChangeAnswer(params, event.target.value)}
              value={params.value}
              disabled={params.row.immutable}
              data-testid={`booleanSelect-${params.row.id}`}
            >
              {params.row.id === 'bulkAnswerRow' ? (
                <MenuItem value="remove" data-testid="option::removeAll">
                  <Typography variant="caption">{t('removeAllAnswers')}</Typography>
                </MenuItem>
              ) : (
                params.row.answer !== '' && (
                  <MenuItem value="remove" data-testid="option::remove">
                    <Typography variant="caption">{t('remove')}</Typography>
                  </MenuItem>
                )
              )}
              <MenuItem value="yes" data-testid="option::yes">
                {yesLabel}
              </MenuItem>
              <MenuItem value="no" data-testid="option::no">
                {noLabel}
              </MenuItem>
              {answerValues.unknown && (
                <MenuItem value="unknown" data-testid="option::unknown">
                  {t('unknown')}
                </MenuItem>
              )}
            </Select>
          )
        },
      },
    ] as GridColDef<BooleanAnswerRow>[]
  }, [yesLabel])

  const details = fieldModels.data?.map(
    field => field.findDetail({ traitId, year }) as ResourceDetail
  )

  const existingDetails = details?.filter(Boolean)

  // handle first fetch of fields
  useEffect(() => {
    if (!fieldModels.isFetched || !gridApi.current || !fieldModels.data) return

    if (!existingDetails) return
    const allDetailsPresent = existingDetails?.length === resourceIds.length

    if (allDetailsPresent) {
      const answers = new Set<BulkAnswer>()
      existingDetails.forEach(detail => {
        answers.add(answerForDetail(detail) as BulkAnswer)
      })
      if (answers.size === 1) {
        setBulkAnswer(Array.from(answers)[0])
      }
    }
  }, [fieldModels.isFetched, setBulkAnswer])

  const rows = useMemo(
    () =>
      !fieldModels.data
        ? []
        : fieldModels.data.map(field => {
            const detail = field.findDetail({ traitId, year })

            return {
              id: field.id,
              field: fieldColumnAttributes(field),
              answer: detail ? answerForDetail(detail) : '',
              immutable: detail?.immutable,
            } as BooleanAnswerRow
          }),
    [fieldModels.isFetched]
  )

  const isAnyDetailImmutable = !!fieldModels.data?.some(
    field => field.findDetail({ traitId, year })?.immutable
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <StyledDataGridPro<BooleanAnswerRow>
        apiRef={gridApi}
        columns={columns}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        hideCellFocus
        rows={rows}
        pinnedRows={
          fieldModels.data?.length && fieldModels.data.length > 1
            ? {
                top: [
                  {
                    id: 'bulkAnswerRow',
                    answer: bulkAnswer || '',
                    immutable: isAnyDetailImmutable,
                  } as BooleanAnswerRow,
                ],
              }
            : undefined
        }
      />
    </Box>
  )
}
